import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';

import { SharedPagination } from '../../shared/SharedPagination/SharedPagination.tsx';
import { SbRespRefineMenuDrawer } from '../SbRespRefineMenuDrawer/SbRespRefineMenuDrawer.tsx';
import { SbRespRefineMenuSelector } from '../SbRespRefineMenuSelector/SbRespRefineMenuSelector.tsx';
import { SbRespQuickFilters } from '../SbRespQuickFilters/SbRespQuickFilters.tsx';
import { SbRespSortItems } from '../SbRespSortItems/SbRespSortItems.tsx';
import { SbRespPageSize } from '../SbRespPageSize/SbRespPageSize.jsx';
import { SbRespBrowseHeader } from '../SbRespBrowseHeader/SbRespBrowseHeader.tsx';
import { SbRespAppliedFilters } from '../SbRespAppliedFilters/SbRespAppliedFilters.jsx';
import { SbRespSearchResultContainer } from '../SbRespSearchResultContainer/SbRespSearchResultContainer.tsx';
import { SbSharedRelatedItems } from '../SbSharedRelatedItems/SbSharedRelatedItems.tsx';
import { SbSharedRecentlySoldItems } from '../SbSharedRecentlySoldItems/SbSharedRecentlySoldItems.tsx';
import { SharedFollowTopButton } from '../../shared/SharedFollowTopButton/SharedFollowTopButton.tsx';

import { useSbSelector } from '../../reducers/useSbSelector.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { pageTypeConstants as pageTypes } from '../../constants/pageTypeConstants.ts';
import { useUpdateRecentSearchResultEntryFromItemSearch } from '../hooks/useUpdateRecentSearchResultEntryFromItemSearch.ts';

import classnames from 'classnames';
import dibsCss from 'dibs-css';
import styles from './SbRespMainContent.module.css';

import { type SbRespMainContent_itemSearch$key } from './__generated__/SbRespMainContent_itemSearch.graphql.ts';
import { type SbRespMainContent_viewer$key } from './__generated__/SbRespMainContent_viewer.graphql.ts';
import { type SbRespMainContent_user$key } from './__generated__/SbRespMainContent_user.graphql.ts';

const viewerFragment = graphql`
    fragment SbRespMainContent_viewer on Viewer {
        ...SbRespRefineMenuSelector_viewer
        ...SbRespSearchResultContainer_viewer
        ...SbSharedRelatedItems_viewer
        ...SbSharedRecentlySoldItems_viewer
        ...SbRespRefineMenuDrawer_viewer
        ...SbRespQuickFilters_viewer
    }
`;

const userFragment = graphql`
    fragment SbRespMainContent_user on User {
        ...SbRespRefineMenuSelector_user
        ...SbRespSearchResultContainer_user
        ...SbRespAppliedFilters_user
        ...SbRespSortItems_user
        ...SharedFollowTopButton_user
        ...SbRespRefineMenuDrawer_user
        ...SbRespQuickFilters_user
    }
`;

const itemSearchFragment = graphql`
    fragment SbRespMainContent_itemSearch on ItemSearchQueryConnection {
        displayUriRef
        pageType
        displayMaxNumberOfPages
        ...useUpdateRecentSearchResultEntryFromItemSearch_itemSearch
        ...SbRespRefineMenuSelector_itemSearch
        ...SbRespSortItems_itemSearch
        ...SbRespPageSize_itemSearch
        ...SbRespBrowseHeader_itemSearch
        ...SbRespSearchResultContainer_itemSearch
        ...SbSharedRelatedItems_itemSearch
        ...SbSharedRecentlySoldItems_itemSearch
        ...SbRespAppliedFilters_itemSearch
        ...SharedFollowTopButton_itemSearch
        ...SbRespRefineMenuDrawer_itemSearch
        ...SbRespQuickFilters_itemSearch
    }
`;

type Props = {
    currency: string;
    itemSearch: SbRespMainContent_itemSearch$key;
    viewer: SbRespMainContent_viewer$key;
    user: SbRespMainContent_user$key | null | undefined;
    isClient?: boolean;
    isThinPage: boolean;
    hideSaveSearchButton?: boolean;
};

export const SbRespMainContent: FC<Props> = ({
    currency,
    itemSearch: itemSearchRef,
    viewer: viewerRef,
    user: userRef,
    isClient,
    isThinPage,
    hideSaveSearchButton,
}) => {
    const { showMeasurements, generatedUriRef, page, isDrawerOpen } = useSbSelector(
        ({ displayPreferences, drawer, filters }) => ({
            showMeasurements: displayPreferences.showMeasurements,
            generatedUriRef: filters.generatedUriRef,
            page: filters.page,
            isDrawerOpen: drawer.isDrawerOpen,
        })
    );
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const { isQuickFiltersRedesignVariant } = useServerVarsContext();

    const { displayUriRef, displayMaxNumberOfPages, pageType } = itemSearch;

    useUpdateRecentSearchResultEntryFromItemSearch({ isClient: !!isClient, itemSearch });

    const sharedProps = {
        currency,
        itemSearch,
        viewer,
        user,
        isClient,
    };

    return (
        <div className={classnames(dibsCss.flex, dibsCss.flexNowrap)}>
            {isQuickFiltersRedesignVariant ? (
                <SbRespRefineMenuDrawer
                    viewer={viewer}
                    itemSearch={itemSearch}
                    user={user}
                    currency={currency}
                    isThinPage={isThinPage}
                />
            ) : (
                <SbRespRefineMenuSelector
                    {...sharedProps}
                    uriRef={generatedUriRef || ''}
                    isThinPage={isThinPage}
                />
            )}
            <div className={classnames(dibsCss.flexGrow, dibsCss.minW1px)}>
                <div
                    className={classnames(
                        dibsCss.flex,
                        dibsCss.justifyBetween,
                        dibsCss.flexRowReverse,
                        dibsCss.gapSmall,
                        {
                            [styles.sticky]: isQuickFiltersRedesignVariant,
                            [styles.drawerOpen]: isQuickFiltersRedesignVariant && isDrawerOpen,
                            [dibsCss.mbLarge]: !isQuickFiltersRedesignVariant,
                            [dibsCss.mtXsmall]: isQuickFiltersRedesignVariant,
                            [dibsCss.itemsCenter]: isQuickFiltersRedesignVariant,
                            [dibsCss.sticky]: isQuickFiltersRedesignVariant,
                            [dibsCss.zHigh]: isQuickFiltersRedesignVariant,
                            [dibsCss.bgWhite]: isQuickFiltersRedesignVariant,
                        }
                    )}
                >
                    <div
                        className={classnames(dibsCss.flex, dibsCss.justifyEnd, {
                            [dibsCss.itemsEnd]: !isQuickFiltersRedesignVariant,
                            [dibsCss.itemsCenter]: isQuickFiltersRedesignVariant,
                        })}
                    >
                        <SbRespSortItems itemSearch={itemSearch} user={user} />
                        <SbRespPageSize itemSearch={itemSearch} />
                    </div>
                    {isQuickFiltersRedesignVariant ? (
                        <SbRespQuickFilters viewer={viewer} itemSearch={itemSearch} user={user} />
                    ) : (
                        <div className={hideSaveSearchButton ? dibsCss.myAuto : ''}>
                            <SbRespBrowseHeader itemSearch={itemSearch} />
                            {!hideSaveSearchButton && (
                                <SharedFollowTopButton itemSearch={itemSearch} user={user} />
                            )}
                        </div>
                    )}
                </div>
                <SbRespAppliedFilters user={user} currency={currency} itemSearch={itemSearch} />
                <div className={classnames(dibsCss.flexGrow, dibsCss.minW1px)}>
                    {/* Item Results */}
                    <SbRespSearchResultContainer
                        {...sharedProps}
                        showMeasurements={showMeasurements}
                    />
                    {/* Related Items */}
                    {isThinPage && (
                        <>
                            <SbSharedRelatedItems viewer={viewer} itemSearch={itemSearch} />
                            <SbSharedRecentlySoldItems viewer={viewer} itemSearch={itemSearch} />
                        </>
                    )}

                    {/* Pagination */}
                    <div className={classnames(dibsCss.my0, dibsCss.mxAuto)}>
                        <SharedPagination
                            currentPage={page}
                            numberOfPages={displayMaxNumberOfPages ?? 0}
                            displayUriRef={displayUriRef || ''}
                            hideHref={pageType === pageTypes.COLLECTION}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
